// ** React Imports
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

// ** MUI Imports
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography
} from '@mui/material'
import { CatalogueCategoryType, CatalogueDataType } from 'src/lib/types/common/catalogue'
import { getCatalogue, getProductById } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { catalogueAdapter } from 'src/lib/utils/adapters'
import {
  filterCatalogueItem,
  findCatalogueItem,
  findCatalogueItemIndex,
  getIndeterminateStatus,
  getSelectionStatus,
  matchCatalogueItemId,
  numberWithCommas
} from 'src/lib/utils/helpers'
import Loader from 'src/components/common/loader-components/Loader'
import { alertSeverites, discountConstants } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Box } from 'react-feather'

interface VariantEditorProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  product: CatalogueDataType | undefined | null
  saveCatalogueData?: (data: CatalogueDataType | undefined) => void
  isEditable?: boolean
}

const VariantEditor: React.FC<VariantEditorProps> = ({
  isOpen,
  setIsOpen,
  product,
  saveCatalogueData,
  isEditable = true
}) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()

  const accountId = merchant?.accountId
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [localSelection, setLocalSelection] = useState<CatalogueDataType[] | undefined | null>([])
  const [fetchedProductData, setFetchedProductData] = useState<CatalogueDataType[] | undefined>([])

  useEffect(() => {
    if (!merchant || !product) return
    callGetProductById()
    Boolean(product?.variants?.length) && setLocalSelection(product?.variants)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, product])

  // Function to fetch all the variants of the selected product from catalogue.
  const callGetProductById = async () => {
    try {
      setShowLoader(true)
      const catalogueResponse: any = await getProductById(issuerId, accountId, product?.id ?? '')

      // When there is an error in fetching the items.
      if (catalogueResponse?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, catalogueResponse.error)
        console.error('x-trace-id:', catalogueResponse.headers?.['x-trace-id'])
        return
      }

      // Adapter for catalogue items.
      const rows: CatalogueDataType[] = catalogueResponse?.data?.data?.variants?.map((row: any) => {
        const rowData = catalogueAdapter(row, 'VARIANT')
        return rowData
      })
      setFetchedProductData(rows)
      setShowLoader(false)
    } catch (e) {
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('alert_item_load_failed'))
    }
  }

  // Item Selection Logics
  const handleSelection = (listItem: CatalogueDataType) => {
    const isItemAlreadySelected = matchCatalogueItemId(localSelection, listItem)
    if (isItemAlreadySelected) {
      setLocalSelection([...(filterCatalogueItem(localSelection, listItem) ?? [])])
      return
    }
    setLocalSelection([...(localSelection ?? []), listItem])
  }

  const handleVariantEditorClose = () => {
    setIsOpen(false)
  }

  const handleVariantSelectionSave = async () => {
    if (!product || !localSelection) return
    const modifiedProduct = { ...(product ?? {}) }
    modifiedProduct.variants = localSelection
    modifiedProduct.allVariantsSelected = Boolean(localSelection?.length === modifiedProduct?.totalVariantCount)
    saveCatalogueData && saveCatalogueData({ ...modifiedProduct })
    handleVariantEditorClose()
  }

  const renderVariantEditor = () => {
    return (
      <div>
        <DialogContent
          id={'scrollable-dialog-content'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 0
          }}
        >
          <List sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }} component='nav'>
            {fetchedProductData?.map((listItem: CatalogueDataType, index: number) => {
              return (
                <div key={`${listItem?.id}_${index}`}>
                  <ListItemButton
                    sx={{
                      width: '100%',
                      border: '0.5px solid #E0E0E0',
                      borderRadius: '6px'
                    }}
                    onClick={() => isEditable && handleSelection(listItem)}
                  >
                    <ListItemIcon>
                      <FloCheckbox
                        isDisabled={!Boolean(isEditable)}
                        value={Boolean(getSelectionStatus(listItem, localSelection, 'VARIANT'))}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={listItem?.id}
                      primary={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className='w-1/2 overflow-hidden text-ellipsis whitespace-nowrap text-coal-dark'>
                            {listItem?.description}
                          </span>
                          <div className='flex items-center justify-between w-1/2 overflow-hidden text-ellipsis whitespace-nowrap text-coal-dark'>
                            <span className='flex justify-end w-1/2'>{listItem?.inventory} available</span>
                            <span>₹ {numberWithCommas((listItem?.price ?? 0).toString())}</span>
                          </div>
                        </div>
                      }
                      primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }}
                    />
                  </ListItemButton>
                </div>
              )
            })}
          </List>
        </DialogContent>
        {showLoader ? (
          <div className='mt-8'>
            <Loader />
          </div>
        ) : (
          <></>
        )}
      </div>
    )
  }

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }
  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <FloGenericDialog
        isOpen={isOpen}
        width={600}
        handleClose={handleVariantEditorClose}
        handleJob={handleVariantSelectionSave}
        jobButtonText={'Done'}
        closeButtonText={'Cancel'}
        content={<>{renderVariantEditor()}</>}
        title={`Edit Variants`}
        subTitle={`for ${product?.description}`}
      />

      {/* Alerts */}
      {openAlert?.length ? (
        <Alerts
          open={Boolean(openAlert?.length)}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default VariantEditor
